import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import OpportunityEditModal from './OpportunityEditModal';
import OpportunityDetailsModal from './OpportunityDetailsModal';
import { Pagination as PaginationType } from '../types';
import { useDebounce, useAbortController } from '../hooks/useDebounce';

interface CompanyOpportunitiesProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
}

interface OpportunityStats {
  total: number;
  active: number;
  published: number;
  moderating: number;
  expired: number;
  draft: number;
  totalApplies: number;
}

interface CompanyCredits {
  opportunities: number;
  highlights: number;
  confidentials: number;
}

interface Opportunity {
  id: number;
  name: string;
  fancy_name?: string;
  status: number;
  published: boolean;
  applies_count: number;
  created_at: string;
  published_at?: string;
  expires_at?: string;
  recruiter_id?: number;
  recruiter_name?: string;
  recruiter_email?: string;
}

const CompanyOpportunities: React.FC<CompanyOpportunitiesProps> = ({ companyId, companyName, onClose }) => {
  const [stats, setStats] = useState<OpportunityStats | null>(null);
  const [credits, setCredits] = useState<CompanyCredits | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const getSignal = useAbortController();
  const [editingOpportunity, setEditingOpportunity] = useState<number | null>(null);
  const [viewingOpportunity, setViewingOpportunity] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);

  useEffect(() => {
    fetchStats();
  }, [companyId]);

  useEffect(() => {
    fetchOpportunities();
  }, [companyId, pagination.page, statusFilter, publishedFilter, debouncedSearch]);

  const fetchStats = async () => {
    try {
      const response = await api.get(`/opportunities/company/${companyId}/stats`);
      setStats(response.data.stats);
      setCredits(response.data.credits);
    } catch (error: any) {
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(publishedFilter && { published: publishedFilter }),
        ...(debouncedSearch && { search: debouncedSearch }),
      });

      const response = await api.get(`/opportunities/company/${companyId}/list?${params}`, { signal: getSignal() });
      setOpportunities(response.data.opportunities);
      setPagination(response.data.pagination);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        toast.error('Erro ao carregar oportunidades');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePublishedFilter = (published: string) => {
    setPublishedFilter(published);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleEditOpportunity = (opportunityId: number) => {
    setEditingOpportunity(opportunityId);
  };

  const handleViewOpportunity = (opportunityId: number) => {
    setViewingOpportunity(opportunityId);
  };

  const toggleDropdown = (opportunityId: number) => {
    setDropdownOpen(dropdownOpen === opportunityId ? null : opportunityId);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownOpen) {
        setDropdownOpen(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  const getStatusLabel = (status: number) => {
    const statusMap: { [key: number]: string } = {
      0: 'Rascunho',
      1: 'Não Paga',
      2: 'Ativa',
      3: 'Desativada'
    };
    return statusMap[status] || 'Desconhecido';
  };

  const getStatusColor = (status: number) => {
    const colorMap: { [key: number]: string } = {
      0: colors.textLight,
      1: '#FFA500',
      2: colors.success,
      3: colors.error
    };
    return colorMap[status] || colors.textLight;
  };

  if (!stats || !credits) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Oportunidades - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        
        <div style={styles.content}>
          {stats && (
            <>
              <h3 style={styles.sectionTitle}>Vagas</h3>
              <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{stats.total}</div>
                  <div style={styles.statLabel}>Total</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: colors.success}}>{stats.active}</div>
                  <div style={styles.statLabel}>Ativas</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: '#4CAF50'}}>{stats.published}</div>
                  <div style={styles.statLabel}>Publicadas</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: '#FF9800'}}>{stats.moderating}</div>
                  <div style={styles.statLabel}>Em Moderação</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: colors.error}}>{stats.expired}</div>
                  <div style={styles.statLabel}>Expirados</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: colors.textLight}}>{stats.draft}</div>
                  <div style={styles.statLabel}>Rascunhos</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: colors.primary}}>{stats.totalApplies}</div>
                  <div style={styles.statLabel}>Candidaturas</div>
                </div>
              </div>
            </>
          )}

          {credits && (
            <>
              <h3 style={styles.sectionTitle}>Saldo de Créditos</h3>
              <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: colors.primary}}>{credits.opportunities}</div>
                  <div style={styles.statLabel}>Vagas</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: '#FFD700'}}>{credits.highlights}</div>
                  <div style={styles.statLabel}>Destaques</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: '#9C27B0'}}>{credits.confidentials}</div>
                  <div style={styles.statLabel}>Confidenciais</div>
                </div>
              </div>
            </>
          )}

          <div style={styles.scopes}>
            <button 
              onClick={() => handleStatusFilter('')} 
              style={{...styles.scopeBtn, ...(statusFilter === '' ? styles.scopeBtnActive : {})}}
            >
              Todos
            </button>
            <button 
              onClick={() => handleStatusFilter('0')} 
              style={{...styles.scopeBtn, ...(statusFilter === '0' ? styles.scopeBtnActive : {})}}
            >
              Rascunho
            </button>
            <button 
              onClick={() => handleStatusFilter('1')} 
              style={{...styles.scopeBtn, ...(statusFilter === '1' ? styles.scopeBtnActive : {})}}
            >
              Não Paga
            </button>
            <button 
              onClick={() => handleStatusFilter('2')} 
              style={{...styles.scopeBtn, ...(statusFilter === '2' ? styles.scopeBtnActive : {})}}
            >
              Ativa
            </button>
            <button 
              onClick={() => handleStatusFilter('3')} 
              style={{...styles.scopeBtn, ...(statusFilter === '3' ? styles.scopeBtnActive : {})}}
            >
              Desativada
            </button>
            <span style={styles.separator}>|</span>
            <button 
              onClick={() => handlePublishedFilter('')} 
              style={{...styles.scopeBtn, ...(publishedFilter === '' ? styles.scopeBtnActive : {})}}
            >
              Todas
            </button>
            <button 
              onClick={() => handlePublishedFilter('true')} 
              style={{...styles.scopeBtn, ...(publishedFilter === 'true' ? styles.scopeBtnActive : {})}}
            >
              Publicadas
            </button>
            <button 
              onClick={() => handlePublishedFilter('false')} 
              style={{...styles.scopeBtn, ...(publishedFilter === 'false' ? styles.scopeBtnActive : {})}}
            >
              Não Publicadas
            </button>
          </div>

          <form onSubmit={handleSearch} style={styles.filters}>
            <input
              type="text"
              placeholder="Buscar por ID ou nome..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={styles.input}
            />
            <button type="submit" style={styles.searchBtn}>
              Buscar
            </button>
          </form>

          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : (
            <>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Nome</th>
                      <th style={styles.th}>Recrutador</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Publicada</th>
                      <th style={styles.th}>Candidaturas</th>
                      <th style={styles.th}>Criada em</th>
                      <th style={styles.th}>Publicada em</th>
                      <th style={styles.th}>Expira em</th>
                      <th style={styles.th}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunities.map((opp) => (
                      <tr key={opp.id} style={styles.tableRow}>
                        <td style={styles.td}>{opp.id}</td>
                        <td style={styles.td}>{opp.fancy_name || opp.name}</td>
                        <td style={styles.td}>{opp.recruiter_name || opp.recruiter_email || '-'}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            backgroundColor: getStatusColor(opp.status)
                          }}>
                            {getStatusLabel(opp.status)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {opp.published ? (
                            <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                          ) : (
                            <span style={{...styles.badge, backgroundColor: colors.error}}>NÃO</span>
                          )}
                        </td>
                        <td style={styles.td}>{opp.applies_count}</td>
                        <td style={styles.td}>
                          {new Date(opp.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td style={styles.td}>
                          {opp.published_at ? new Date(opp.published_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td style={styles.td}>
                          {opp.expires_at ? new Date(opp.expires_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionsCell}>
                            <div style={styles.dropdown}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDropdown(opp.id);
                                }}
                                style={styles.actionBtn}
                                title="Ações"
                              >
                                ⋮
                              </button>
                              {dropdownOpen === opp.id && (
                                <div style={styles.dropdownMenu}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewOpportunity(opp.id);
                                      setDropdownOpen(null);
                                    }}
                                    style={styles.dropdownItem}
                                  >
                                    Ver Detalhes
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditOpportunity(opp.id);
                                      setDropdownOpen(null);
                                    }}
                                    style={{
                                      ...styles.dropdownItem,
                                    }}
                                  >
                                    Editar
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {opportunities.length === 0 && (
                  <div style={styles.emptyState}>Nenhuma oportunidade encontrada</div>
                )}
              </div>

              <div style={styles.paginationContainer}>
                <div style={styles.paginationInfo}>
                  Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
                </div>
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
        
        {editingOpportunity && (
          <OpportunityEditModal
            opportunityId={editingOpportunity}
            onClose={() => setEditingOpportunity(null)}
            onSave={() => {
              setEditingOpportunity(null);
              fetchOpportunities();
            }}
          />
        )}
        
        {viewingOpportunity && (
          <OpportunityDetailsModal
            opportunityId={viewingOpportunity}
            onClose={() => setViewingOpportunity(null)}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  statCard: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '0.5rem',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  sectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '1rem',
    marginTop: '1rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  separator: {
    color: colors.textLight,
    fontSize: '1.2rem',
    margin: '0 0.5rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'auto',
    border: `1px solid ${colors.border}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  badge: {
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    display: 'inline-block',
    textWrap: "nowrap",
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: colors.textLight,
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  actionsCell: {
    textAlign: 'center' as const,
    position: 'relative' as const,
    minWidth: '80px',
  },
  dropdown: {
    position: 'relative' as const,
    display: 'inline-block',
  },
  dropdownMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
};

export default CompanyOpportunities;
